import { ElementRef, inject, Injectable, Renderer2 } from '@angular/core';
import * as joint from '@clientio/rappid';
import { TranslocoService } from '@jsverse/transloco';
import { BoolValue, LinkAttributeConverter } from 'shared/domain';
import { calculateRichTextHeightForJointJs, calculateTextWidth } from 'shared/util-common';

import { CustomisedElementId } from '@treetop/shared/color-presets';
import {
  FlowchartActionDto,
  FlowchartDecisionNodeDto,
  FlowchartElementType,
  FlowchartLinkAttributeDto,
  FlowchartLinkValueType,
  FlowchartNodeStyleDto,
  FlowchartStartNodeDto,
  FlowchartTextBlockDto,
  LinkOffsetDto,
  SelectionBehavior,
  TimeSpanDto,
  TimeSpanType,
} from '@treetop/shared/model';

import { ActionNodeService } from './action-node.service';
import {
  FLOWCHART_ATTACHMENT_SVG,
  FLOWCHART_COMMENT_SVG,
  FLOWCHART_LINK_SVG,
  FLOWCHART_MEDICATION_SVG,
  FLOWCHART_NOTE_SVG,
  FLOWCHART_RECURRENCE_INFINITE_SVG,
  FLOWCHART_REFERENCE_SVG,
} from './cached-icons';
import { ContainerResize } from './container/ContainerResize';
import { ContainerNodeService } from './container-node.service';
import { DecisionNodeService } from './decision-node.service';
import { getGridMatchedPositionValue } from './grid.util';
import { PositionService } from './position.service';
import { SelectionBehaviorNodeService } from './selection-behavior-node.service';
import { SizeService } from './size.service';
import { CreateStartNodeOptions, StartNodeService } from './start-node.service';
import SubflowService from './subflow.service';
import { FlowchartTextBlockNode } from '../../entities/FlowchartTextBlockNode';
import { IconTagTypes } from '../../model/tag-types.model';
import { markLinkAsSelected } from '../../utils/links';
import { FlowchartConfig, FontAttributes, FontAttributesTags, MINIMUM_INCOMING_LINK_LENGTH } from '../base/flowchart.config';
import { BaseGraph } from '../flowchart/graph/BaseGraph';
import { FlowchartModelService } from '../flowchart/model.service';
import { BasePaper } from '../flowchart/paper/BasePaper';
import { FlowchartScrollerService } from '../flowchart/scroller.service';
import { FlowchartViewService } from '../flowchart/view.service';
import { HeaderElement } from '../shapes/shared-header';
import { getHeightOfSubComponent } from './container/ContainerSubComponentCalculator';
import { HtmlUtil } from '../../util/html.util';

const AUTO_SCROLL_HORIZONTAL_MARGIN = FlowchartConfig.paper.gridSize * 5;
const AUTO_SCROLL_VERTICAL_MARGIN = FlowchartConfig.paper.gridSize * 5;

const ELEMENT_TYPE_TO_CUSTOMISED_ID: Record<FlowchartElementType, CustomisedElementId> = {
  [FlowchartElementType.FlowchartStartNode]: 'start',
  [FlowchartElementType.FlowchartAction]: 'action',
  [FlowchartElementType.FlowchartDecisionNode]: 'decision',
  [FlowchartElementType.FlowchartCondition]: 'decision',
  [FlowchartElementType.FlowchartSelectionBehavior]: 'selection-behavior',
  [FlowchartElementType.FlowchartLink]: 'link-label',
  [FlowchartElementType.FlowchartTextBlock]: 'text-block',
  [FlowchartElementType.FlowchartSubflow]: 'container',
  [FlowchartElementType.FlowchartContainer]: 'container',
};

export interface LinkAnchorData {
  anchor?: {
    args?: {
      dx: string;
      dy: string;
      rotate?: boolean;
      offset?: number;
    };
  };
  name: string;
}

@Injectable()
class OperationsService {
  private flowchartViewService = inject(FlowchartViewService);
  private flowchartModelService = inject(FlowchartModelService);
  private flowchartScrollerService = inject(FlowchartScrollerService);
  private translocoService = inject(TranslocoService);
  private positionService = inject(PositionService);
  private sizeService = inject(SizeService);
  public startNodeService = inject(StartNodeService);
  public actionNodeService = inject(ActionNodeService);
  public decisionNodeService = inject(DecisionNodeService);
  public selectionBehaviorNodeService = inject(SelectionBehaviorNodeService);
  private subflowService = inject(SubflowService);

  paperSource: BasePaper;
  graphSource: BaseGraph;
  scrollerSource: joint.ui.PaperScroller;
  renderer: Renderer2;
  el: ElementRef;

  constructor() {
    this.paperSource = this.flowchartViewService.getPaper();
    this.graphSource = this.flowchartModelService.getGraph();
    this.scrollerSource = this.flowchartScrollerService.getScroller();
  }

  setDOMReferences(renderer: Renderer2, el: ElementRef): void {
    this.renderer = renderer;
    this.el = el;
  }

  createStart(text: string, options?: Partial<CreateStartNodeOptions>): joint.dia.Cell {
    return this.startNodeService.createStart(text, options);
  }

  public createStandaloneActionNode(title: string, x: number, y: number): joint.dia.Cell {
    return this.actionNodeService.createActionNode(title, { x, y });
  }

  public createStandaloneDecisionNode(title: string, x: number, y: number): joint.dia.Cell {
    return this.decisionNodeService.createDecision(title, { x, y });
  }

  public createStandaloneSelectionBehaviorNode(x: number, y: number): joint.dia.Cell {
    return this.selectionBehaviorNodeService.createSelectionBehaviorNode(this.paperSource.paper, SelectionBehavior.And, x, y);
  }

  public createStandaloneContainerNode(x: number, y: number): joint.dia.Cell {
    return ContainerNodeService.createContainer(this.paperSource.paper, { x, y });
  }

  public createStandaloneSubflowNode(x: number, y: number): joint.dia.Cell {
    return this.subflowService.createSubflow(x, y);
  }

  public createStandaloneTextBlockNode(x: number, y: number): joint.dia.Cell {
    return FlowchartTextBlockNode.create(this.graphSource.graph, {
      x,
      y,
      placeholder: this.translocoService.translate(FlowchartConfig.textBlockNode.placeholder),
    });
  }

  public createContainerHeader(title: string, x: number, y: number): joint.dia.Cell {
    return ContainerNodeService.createContainerHeader(this.paperSource.paper, { text: title, x, y });
  }

  public createSubflowHeader(title: string, x: number, y: number): joint.dia.Cell {
    return this.subflowService.createSubflowHeader(title, x, y);
  }

  public addSubflow(
    sourceElement: joint.dia.ElementView,
    sourceAnchor: LinkAnchorData,
    targetAnchor: LinkAnchorData,
    subflow: joint.dia.Cell,
    title: string,
  ): void {
    const subflowHeader = this.subflowService.createSubflowHeader(title, subflow.attributes.position.x, subflow.attributes.position.y);
    this.graphSource.graph.addCell(subflow);
    this.graphSource.graph.addCell(subflowHeader);
    subflow.embed(subflowHeader);
    const newLink = this.createLink(sourceElement?.model, subflow, sourceAnchor, targetAnchor);
    if (newLink) {
      this.graphSource.graph.addCell(newLink);
    }
    this.paperSource.paper.fitToContent({ useModelGeometry: true, padding: 10, allowNewOrigin: 'any' });
  }

  createActionNodeInDefaultPosition(
    sourceElement: joint.dia.ElementView,
    sourceAnchor: LinkAnchorData,
    overwrittenPosition?: joint.g.PlainPoint,
    colorPresetId?: string,
  ): joint.dia.Cell {
    return this.actionNodeService.createActionNodeInDefaultPosition(sourceElement, sourceAnchor, overwrittenPosition, colorPresetId);
  }

  addActionNode(
    sourceElement: joint.dia.ElementView,
    sourceAnchor: LinkAnchorData,
    targetAnchor: LinkAnchorData,
    newElement: joint.dia.Cell,
    isSubflow: boolean,
    isSubflowLinkedElement: boolean,
  ): void {
    if (isSubflow) {
      this.handleSubflowOperations(sourceElement, newElement, sourceAnchor, targetAnchor, isSubflowLinkedElement);
    } else {
      this.handleNonSubflowOperations(sourceElement, newElement, sourceAnchor, targetAnchor);
    }
    this.fitPaperToContent();
  }

  createSelectionBehaviorNodeInDefaultPosition(
    sourceElement: joint.dia.ElementView,
    sourceAnchor: LinkAnchorData,
    overwrittenPosition?: joint.g.PlainPoint,
  ): joint.dia.Cell {
    return this.selectionBehaviorNodeService.createSelectionBehaviorNodeInDefaultPosition(
      this.paperSource.paper,
      sourceElement,
      sourceAnchor,
      overwrittenPosition,
    );
  }

  addSelectionBehaviorNode(
    sourceElement: joint.dia.ElementView,
    sourceAnchor: LinkAnchorData,
    targetAnchor: LinkAnchorData,
    newElement: joint.dia.Cell,
    isSubflow: boolean,
    isSubflowLinkedElement: boolean,
  ): void {
    if (isSubflow) {
      this.handleSubflowOperations(sourceElement, newElement, sourceAnchor, targetAnchor, isSubflowLinkedElement);
    } else {
      this.handleNonSubflowOperations(sourceElement, newElement, sourceAnchor, targetAnchor);
    }
    this.fitPaperToContent();
  }

  createDefaultLink(): joint.shapes.standard.Link {
    return new joint.shapes.standard.Link({
      z: 2,
      attrs: {
        line: {
          stroke: '#d0dce7', // St
          class: 'link__line',
          targetMarker: {
            type: 'path',
            class: 'link__arrowhead',
            d: `M 0 0 L 8 8 Z M 0 0 L 8 -8 Z`,
            'stroke-linejoin': 'round',
          },
        },
        outline: {
          class: 'link__outline',
          connection: true,
          stroke: '#d0dce7', // Stroke color (teal as example)
        },
      },
      labels: [
        {
          position: 0.5,
          markup: [
            {
              tagName: 'rect',
              selector: 'body',
            },
            {
              tagName: 'text',
              selector: 'label',
            },
          ],
          attrs: {
            label: {
              ...FontAttributes,
              class: 'link__label__value__text',
              text: '',
              fontSize: 13,
              fill: '#063D70',
              textAnchor: 'middle',
              yAlignment: 'middle',
              display: 'none',
            },

            body: {
              class: 'link__label__value__body',
              ref: 'label',
              fill: '#FFC1EA',
              xAlignment: 'middle',
              yAlignment: 'middle',
              rx: '10',
              refX: 0.5,
              refY: 0.5,
              refWidth: 16,
              refHeight: 4,
              display: 'none',
            },
          },
        },
        {
          position: 0.9,
          markup: [
            {
              tagName: 'rect',
              selector: 'body',
            },
            {
              tagName: 'text',
              selector: 'label',
            },
          ],
          attrs: {
            label: {
              ...FontAttributes,
              class: 'link__label__offset__text',
              text: 'offset',
              fill: '#d0dce7',
              textAnchor: 'middle',
              yAlignment: 'middle',
              display: 'none',
              fontSize: 13,
              fontWeight: 400,
            },
            body: {
              class: 'link__label__offset__body',
              ref: 'label',
              fill: '#FFF',
              x: `calc(x-${FlowchartConfig.spacing - FlowchartConfig.bevel})`,
              y: `calc(y-${FlowchartConfig.spacing - FlowchartConfig.bevel})`,
              width: `calc(w+${FlowchartConfig.spacing - FlowchartConfig.bevel})`,
              height: `calc(h+${FlowchartConfig.spacing - FlowchartConfig.bevel})`,
              display: 'none',
            },
          },
        },
      ],
      markup: [
        {
          tagName: 'path',
          selector: 'wrapper',
          attributes: {
            fill: 'none',
            cursor: 'pointer',
            stroke: 'transparent',
            'stroke-linecap': 'round',
          },
        },
        {
          tagName: 'path',
          selector: 'outline',
          attributes: {
            fill: 'none',
            'pointer-events': 'none',
          },
        },
        {
          tagName: 'path',
          selector: 'line',
          attributes: {
            fill: 'none',
            'pointer-events': 'none',
          },
        },
      ],
    });
  }

  createLink(
    source: joint.dia.Cell,
    target: joint.dia.Cell,
    sourceAnchor: LinkAnchorData = { name: 'bottom' },
    targetAnchor: LinkAnchorData = { name: 'top' },
    id?: string,
  ): joint.dia.Cell {
    if (!source || !target) {
      return;
    }

    const link = this.createDefaultLink();

    link.on('change:target', (link: joint.dia.Link, target: joint.dia.Link.EndJSON) => {
      const cell = this.graphSource.graph.getCell(target.id);

      if (cell?.attributes.isContainer && target.anchor?.name === 'top') {
        const dy = cell.attributes.hasTwoLinesTitle ? -FlowchartConfig.containerNode.headerSizeBig.padding : 0;
        const newTarget = {
          id: target.id,
          anchor: {
            name: target.anchor.name,
            args: { dy: dy },
          },
        };

        link.target(newTarget);
      }
    });

    link.source({ id: source.id, anchor: sourceAnchor });
    link.target({ id: target.id, anchor: targetAnchor });

    if (id) {
      link.set('id', id);
    }

    link.attr('line/uniqueId', link.id);

    return link;
  }

  setClickableForSmartbook(id: string | number, description) {
    if (description && description !== '' && id) {
      const element = this.graphSource.graph.getCell(id);
      const currentClass = element.attributes.attrs.body?.class;
      element.prop('attrs/body/class', currentClass + ' clickable');
    }
  }

  initElementsDescription(id?: string, text?: string) {
    this.removeElementsDescription(id);
    if (text?.length > 0) {
      this.createElementsDescription(id, text);
    }
  }

  private createElementsDescription(id?: string, text?: string) {
    const element = this.graphSource.graph.getCell(id);
    if (text?.length > 0 && element) {
      const elementHeight = element.attributes.size.height as number;
      const hasTags = element?.getEmbeddedCells()?.find((embed) => embed.attributes.isElementTag);
      const descriptionWidth = element.attributes.size.width - 40;
      const y = hasTags ? element.attributes.position.y + 55 : element.attributes.position.y + 45;
      const Description = joint.dia.Element.define(
        'example.ForeignObject',
        {
          position: {
            x: element.attributes.position.x + (element.attributes.size.width - descriptionWidth) / 2,
            y: getGridMatchedPositionValue(y),
          },
          attrs: {
            foreignObject: {
              width: descriptionWidth,
              height: element.attributes.parentContainerId
                ? this.baseHeightContainer(HtmlUtil.sanitizeHTMLForSVG(text), descriptionWidth)
                : this.baseHeight(elementHeight),
              fontFamily: "'Roboto', 'Helvetica Neue', sans-serif",
              fontSize: '14px',
            },
          },
        },
        {
          markup: joint.util.svg`
              <rect @selector="body"/>
              <foreignObject @selector="foreignObject" class="element--description__foreignObject normal no-margin no-padding">
              ${HtmlUtil.sanitizeHTMLForSVG(text)}
              </foreignObject>
          `,
        },
      );

      const description = new Description();
      description.set('isElementDescription', true);
      description.set('parentElementId', element.attributes.id);
      this.graphSource.graph.addCell(description);
      element.embed(description);

      if (element instanceof joint.dia.Element && element.attributes.parentContainerId) {
        element.resize(element.attributes.size.width, getHeightOfSubComponent(element));
        const container = this.graphSource.graph.getCell(element.attributes.parentContainerId);
        ContainerResize.adjustContainerSizeOnEmbedChange(container as joint.dia.Element, this.paperSource.paper);
        ContainerResize.repositionContainerElements(container as joint.dia.Element, this.paperSource.paper);
      }

      if (element instanceof joint.dia.Element && elementHeight < 100 && !element.attributes.parentContainerId) {
        element.resize(element.attributes.size.width, 100);
      }

      this.setLabelPosition(id);

      const graph = this.graphSource.graph;

      description.on('change:position', (element, newPositon, opt) => {
        if (element.attributes.descriptionClicked) {
          const parent = graph.getCell(element.attributes.parent);
          const currentParentPosition = parent.attributes.position;
          const xP = currentParentPosition.x;
          const yP = currentParentPosition.y;
          (parent as joint.dia.Element).position(getGridMatchedPositionValue(xP + opt.tx), getGridMatchedPositionValue(yP + opt.ty));
          // Because the {depth: true} flag also aims the current "element", which causes infinite loop.
          parent.getEmbeddedCells().forEach((el) => {
            if (el.attributes.id !== element.attributes.id) {
              const position = (el as joint.dia.Element).position();
              (el as joint.dia.Element).position(position.x + opt.tx, position.y + opt.ty);
            }
          });
        }
      });

      element.on('change:size', (_element: joint.dia.Element, _newSize: joint.dia.Size) => {
        const hasDescription = element?.getEmbeddedCells()?.find((embed) => embed.attributes.isElementDescription);
        if (hasDescription) {
          const newWidth = _newSize.width - 40;
          const elementHeight = _newSize.height as number;
          const height = element.attributes.parentContainerId
            ? this.baseHeightContainer(HtmlUtil.sanitizeHTMLForSVG(text), newWidth)
            : this.baseHeight(elementHeight);

          description.attr({
            foreignObject: {
              width: newWidth,
              height: height,
            },
          });

          const hasTags = element?.getEmbeddedCells()?.find((embed) => embed.attributes.isElementTag);
          const y = hasTags ? element.attributes.position.y + 55 : element.attributes.position.y + 45;

          description.position(element.attributes.position.x + (_newSize.width - newWidth) / 2, getGridMatchedPositionValue(y));

          if (element instanceof joint.dia.Element && elementHeight < 100) {
            element.resize(_newSize.width, 100);
          }
        }
      });
    }
  }

  private baseHeightContainer(text: string, width?: number) {
    //todo remove hardcoded font
    return calculateRichTextHeightForJointJs(HtmlUtil.sanitizeHTMLForSVG(text), 'Roboto', '14', '1', width);
  }

  private baseHeight(elementHeight: number) {
    // TODO: Remove hard coded numbers, instead use a "Content driven element" to adjust these.
    const descriptionHeight = elementHeight - 60;
    const minimumDescriptionHeight = 24;
    return descriptionHeight < minimumDescriptionHeight ? minimumDescriptionHeight : descriptionHeight;
  }

  removeElementsDescription(id: string) {
    const element = this.graphSource.graph.getCell(id);
    const embeddedElements = element?.getEmbeddedCells();

    if (embeddedElements?.length > 0) {
      const descriptionEmbedded = [];
      embeddedElements.forEach((embed) => {
        if (embed.attributes?.isElementDescription) {
          descriptionEmbedded.push(embed);
          this.graphSource.graph.removeCells(descriptionEmbedded);
          element.off('change:size.description');
          this.setLabelPosition(id);
        }
      });
    }
  }

  createDecisionInDefaultPosition(
    sourceElement: joint.dia.ElementView,
    sourceAnchor: LinkAnchorData,
    overwrittenPosition?: joint.g.PlainPoint,
  ): joint.dia.Cell {
    return this.decisionNodeService.createDecisionInDefaultPosition(sourceElement, sourceAnchor, overwrittenPosition);
  }

  addDecision(
    sourceElement: joint.dia.ElementView,
    sourceAnchor: LinkAnchorData,
    targetAnchor: LinkAnchorData,
    newElement: joint.dia.Cell,
    isSubflow: boolean,
    isSubflowLinkedElement: boolean,
  ): void {
    if (isSubflow) {
      this.handleSubflowOperations(sourceElement, newElement, sourceAnchor, targetAnchor, isSubflowLinkedElement);
    } else {
      this.handleNonSubflowOperations(sourceElement, newElement, sourceAnchor, targetAnchor);
    }
    this.fitPaperToContent();
  }

  createContainerInDefaultPosition(
    sourceElement: joint.dia.ElementView,
    sourceAnchor: LinkAnchorData,
    overwrittenPosition?: joint.g.PlainPoint,
  ): joint.dia.Cell {
    return ContainerNodeService.createContainerInDefaultPosition(
      this.paperSource.paper,
      sourceElement,
      sourceAnchor,
      this.positionService,
      overwrittenPosition,
    );
  }

  addSubComponentToContainer(containerId: string, subElement: joint.dia.Cell) {
    const container = this.graphSource.graph.getCell(containerId);
    this.graphSource.graph.addCell(subElement);
    container.embed(subElement);
    this.addTooltip(subElement.id as string);
  }

  exchangeEmbeddedOrder(id: string, elementId: string, exchangeId: string) {
    const parentElement = this.paperSource.paper.findViewByModel(id) as joint.dia.ElementView;
    const embedded = parentElement.model.get('embeds');
    const index1 = embedded.findIndex((el) => el === elementId);
    const index2 = embedded.findIndex((el) => el === exchangeId);

    [embedded[index1], embedded[index2]] = [embedded[index2], embedded[index1]]; // Swap the first two elements
    parentElement.model.set('embeds', embedded);
    parentElement.model.trigger('change:embeds', parentElement.model, embedded);
  }

  addTooltip(id: string) {
    const element = this.paperSource.paper.findViewByModel(id);
    element.$el.attr('data-tooltip', element.model.attributes.attrs.label.text);
  }

  updateSelectionBehaviorForSubActions(element: FlowchartActionDto) {
    const container = this.graphSource.graph.getCell(element.id);
    const subActions = element.children;

    //remove old labels
    const embeddedCells = container.getEmbeddedCells();
    embeddedCells.forEach((embeddedElement) => {
      if (embeddedElement.attributes.isContainerSelectionBehavior) {
        embeddedElement.remove();
      }
    });

    const selectionBehavior = element.guidelineAction?.selectionBehaviorForChildren;

    //recreate labels
    subActions.forEach((el, index) => {
      if (index !== subActions.length - 1) {
        const subAction = this.graphSource.graph.getCell(el.id);

        const containerSelectionBehavior = ContainerNodeService.createContainerSelectionBehavior(
          selectionBehavior,
          this.translocoService,
          subAction.attributes.position.x,
          subAction.attributes.position.y,
          subAction.attributes.size.height,
          subAction.attributes.size.width,
        );
        this.graphSource.graph.addCell(containerSelectionBehavior);
        container.embed(containerSelectionBehavior);
      }
    });
  }

  addContainer(
    sourceElement: joint.dia.ElementView,
    sourceAnchor: LinkAnchorData,
    targetAnchor: LinkAnchorData,
    container: joint.dia.Cell,
    title: string,
    isSubflow: boolean,
    isSubflowLinkedElement: boolean,
  ): void {
    if (isSubflow) {
      this.handleSubflowOperations(sourceElement, container, sourceAnchor, targetAnchor, isSubflowLinkedElement);
    } else {
      this.handleNonSubflowOperations(sourceElement, container, sourceAnchor, targetAnchor);
    }

    const containerHeader = ContainerNodeService.createContainerHeader(this.paperSource.paper, {
      text: title,
      x: container.attributes.position.x,
      y: container.attributes.position.y,
    });

    this.graphSource.graph.addCell(containerHeader);
    container.embed(containerHeader);

    this.fitPaperToContent();
  }

  handleSubflowOperations(
    sourceElement: joint.dia.ElementView,
    elementToAdd: joint.dia.Cell,
    sourceAnchor: LinkAnchorData,
    targetAnchor: LinkAnchorData,
    isSubflowLinkedElement: boolean,
  ): void {
    let subflowParent;
    const parentElementId = sourceElement.model.attributes.subflowParentId;
    if (parentElementId) {
      subflowParent = this.graphSource.graph.getCell(parentElementId);
    } else {
      subflowParent = this.graphSource.graph.getCell(sourceElement.model.id);
    }

    (subflowParent as joint.dia.Element).embed(elementToAdd);
    this.graphSource.graph.addCell(elementToAdd);

    this.sizeService.resizeParentToFit(subflowParent, this.graphSource.graph);

    if (isSubflowLinkedElement) {
      const newLink = this.createLink(sourceElement.model, elementToAdd, sourceAnchor, targetAnchor);
      this.graphSource.graph.addCell(newLink);
    } else {
      elementToAdd.set('position', {
        x: sourceElement.model.attributes.position.x + (sourceElement.model.attributes.size.width - elementToAdd.attributes.size.width) / 2,
        y: sourceElement.model.attributes.position.y + 60,
      });
      if (elementToAdd.attributes.isTextBlock) {
        const foreignObject = elementToAdd.getEmbeddedCells().find((embed) => embed.attributes?.isTextBlockContent);
        foreignObject.prop('position', {
          x: elementToAdd.attributes.position.x + FlowchartConfig.textBlockNode.offsetLeft,
          y: elementToAdd.attributes.position.y + FlowchartConfig.textBlockNode.offsetTop,
        });
      }
    }

    const header = subflowParent.getEmbeddedCells()[0];
    (header as HeaderElement).resizeShape();
  }

  handleNonSubflowOperations(
    sourceElement: joint.dia.ElementView | undefined,
    elementToAdd: joint.dia.Cell,
    sourceAnchor: LinkAnchorData,
    targetAnchor: LinkAnchorData,
  ): void {
    this.graphSource.graph.addCell(elementToAdd);
    if (sourceElement) {
      const newLink = this.createLink(sourceElement.model, elementToAdd, sourceAnchor, targetAnchor, null);
      this.graphSource.graph.addCell(newLink);
    }
  }

  recreateLinks(nodeToRemove: joint.dia.Cell) {
    const links = this.graphSource.graph.getConnectedLinks(nodeToRemove);

    const previousElement = [];
    const targetElements = [];
    const targetAnchors = [];
    const newLinks = [];
    for (const link of links) {
      const source = link.get('source');
      const sourceElement = this.graphSource.graph.getCell(source.id);
      const sourceAnchor = source.anchor;
      const targetElement = link.getTargetElement();
      const targetAnchor = link.get('target').anchor;

      if (targetElement === nodeToRemove) {
        previousElement.push({
          element: sourceElement,
          anchor: sourceAnchor,
        });
      } else if (sourceElement === nodeToRemove) {
        targetElements.push(targetElement);
        targetAnchors.push(targetAnchor);
      }
    }

    if (targetElements.length > 0) {
      targetElements.forEach((targetElement, index) => {
        if (previousElement && targetElement && targetAnchors[index]) {
          previousElement.forEach((prevElement) => {
            const prevElementOutgoingLinks = this.graphSource.graph.getConnectedLinks(prevElement.element, { outbound: true });
            const checkForExistingLink = prevElementOutgoingLinks.find(
              (link) => link.attributes.source.id === prevElement.element.id && link.attributes.target.id === targetElement.id,
            );
            if (!checkForExistingLink) {
              const newLink = this.createLink(
                prevElement.element,
                targetElement,
                { name: prevElement.anchor.name },
                { name: targetAnchors[index].name },
              );
              this.graphSource.graph.addCell(newLink);
              newLinks.push(newLink);
            }
          });
        }
      });
    }

    nodeToRemove.remove();
    return newLinks;
  }

  public editColorPreset(colorPresetId: string, elementId: string): void {
    const cell = this.graphSource.graph.getCell(elementId);

    const elementView = this.paperSource.paper.findViewByModel(cell);
    const containerView = this.paperSource.paper.findViewByModel(cell);
    const headerContainerId = containerView?.model.attributes.embeds?.[0];
    const headerContainer = this.paperSource.paper.findViewByModel(headerContainerId);

    if (!colorPresetId) {
      elementView?.model.removeAttr('body/fill');
      elementView?.model.removeAttr('body/stroke');
      elementView?.model.removeAttr('label/fill');

      if (!headerContainer?.model.attributes.isElementTag) {
        headerContainer?.model.removeAttr('body/fill');
        headerContainer?.model.removeAttr('body/stroke');
        headerContainer?.model.removeAttr('label/fill');
      }

      return;
    }

    elementView?.model.attr('body/fill', `var(--tt-color-preset-${colorPresetId}-fill) !important`);
    elementView?.model.attr('body/stroke', `var(--tt-color-preset-${colorPresetId}-stroke) !important`);
    elementView?.model.attr('label/fill', `var(--tt-color-preset-${colorPresetId}-text) !important`);

    if (!headerContainer?.model?.attributes?.isElementTag) {
      headerContainer?.model?.attr('body/fill', `var(--tt-color-preset-${colorPresetId}-fill) !important`);
      headerContainer?.model?.attr('body/stroke', `var(--tt-color-preset-${colorPresetId}-stroke) !important`);
      headerContainer?.model?.attr('label/fill', `var(--tt-color-preset-${colorPresetId}-text) !important`);
    }
  }

  editTitle(title: string, id: string) {
    const elementView = this.paperSource.paper.findViewByModel(this.graphSource.graph.getCell(id));

    if (!title || title.trim().length === 0) {
      title = this.translocoService.translate(FlowchartConfig.defaultTitleKey);
    }

    if (elementView && elementView.model.attributes.isSubflow) {
      //edit subflow header

      this.editHeaderTitle(title, id);
    } else if (elementView && title !== null) {
      elementView.model.attr('label/text', title);
    }
  }

  editTextBox(text: string, customStyling: FlowchartNodeStyleDto, id: string) {
    const elementView = this.paperSource.paper.findViewByModel(this.graphSource.graph.getCell(id));
    if (elementView) {
      const foreignObject = elementView.model.getEmbeddedCells().find((embed) => embed.attributes?.isTextBlockContent);
      //set to null first to avoid issue with incorrect content
      foreignObject.prop('markup', null);

      const sanitizedHtml = text
        ? HtmlUtil.sanitizeHTMLForSVG(text)
        : '<span class="element--textblock__placeholder">' +
          this.translocoService.translate(FlowchartConfig.textBlockNode.placeholder) +
          '</span>';

      const contentAsSvg = joint.util.svg`
              <rect @selector="body"/>
              <foreignObject @selector="foreignObject">${sanitizedHtml}</foreignObject>
          `;

      foreignObject.prop('markup', contentAsSvg);
      if (customStyling) {
        if (customStyling.borderColor) {
          elementView.model.prop('attrs/body/stroke', customStyling.borderColor);
        }
        if (customStyling.backgroundColor) {
          elementView.model.prop('attrs/body/fill', customStyling.backgroundColor);
        }
        if (customStyling.borderStyle) {
          elementView.model.prop(
            'attrs/body/strokeDasharray',
            customStyling.borderStyle == 'solid' ? '' : customStyling.borderStyle == 'dashed' ? '5 5' : '2',
          );
        }
      }
    }
  }

  editHeaderTitle(title: string, id: string) {
    const container = this.graphSource.graph.getCell(id);
    const containerView = this.paperSource.paper.findViewByModel(container);
    // After save containerView is null some times, so we need to check
    if (containerView) {
      const embeds = containerView.model.attributes.embeds;
      if (embeds) {
        this.editTitle(title, embeds[0]);
      }
    }
  }

  editSelectionBehavior(selectionbehavior: SelectionBehavior, id: string) {
    const container = this.graphSource.graph.getCell(id);
    const embeddedCells = container.getEmbeddedCells();

    if (embeddedCells.length > 0) {
      embeddedCells.forEach((embeddedElement) => {
        if (embeddedElement.attributes.isContainerSelectionBehavior) {
          const text = this.translocoService.translate(SelectionBehavior[selectionbehavior]);
          embeddedElement.attr('label/text', text);
        }
      });
    }
  }

  setLinkAttribute(attribute: FlowchartLinkAttributeDto, id: string, unitName?: string) {
    const link = this.graphSource.graph.getCell(id) as joint.dia.Link;
    if (!link) return;

    if (attribute?.position) {
      link.label(0, {
        position: attribute?.position,
      });
    }

    let attributeText = '';
    if (attribute?.value) {
      const linkEntry = Object.entries(attribute.value).filter(([_, value]) => value !== null);
      if (linkEntry[0]) {
        const [linkValueType, linkValue] = linkEntry[0];
        if (linkValueType == FlowchartLinkValueType.codeableConceptValueArray) {
          const operatorString = LinkAttributeConverter.convertOperatorToValue(attribute.operator);
          attributeText =
            operatorString !== '=' ? operatorString + ' ' + Object.values(linkValue).join('; ') : Object.values(linkValue).join('; ');
        } else if (linkValueType == FlowchartLinkValueType.numericalValue) {
          const operatorString = LinkAttributeConverter.convertOperatorToValue(attribute.operator);
          attributeText = operatorString + ' ' + linkValue.toString();
        } else if (linkValueType == 'textValue') {
          attributeText = linkValue.toString();
        } else if (linkValueType == 'booleanValue') {
          attributeText = (linkValue as boolean)
            ? this.translocoService.translate(BoolValue.true)
            : this.translocoService.translate(BoolValue.false);
        } else if (linkValueType == FlowchartLinkValueType.numericalValueRange) {
          attributeText = linkValue?.item1?.toString() + '-' + linkValue?.item2?.toString();
        }
      }
    }

    if (attributeText && unitName) {
      attributeText = attributeText + ' ' + unitName;
    }

    const linkView = link.findView(this.paperSource.paper);
    if (attributeText) {
      link.prop(['labels', 0, 'attrs', 'label', 'text'], attributeText);
      this.showLabel(true, id, 0);
    } else {
      this.showLabel(false, id, 0);
    }

    if (link.attributes.selected) {
      link.prop(['labels', 0, 'attrs', 'label', 'text'], attributeText);

      if (linkView instanceof joint.dia.LinkView) {
        markLinkAsSelected(linkView);
      }

      if (attributeText) {
        this.showLabel(true, id, 0);
      } else {
        this.showLabel(false, id, 0);
      }
    }
  }

  setLinkOffset(linkOffset: LinkOffsetDto, id: string) {
    const link = this.graphSource.graph.getCell(id) as joint.dia.Link;

    if (!link) return;

    if (linkOffset?.position) {
      link.label(1, {
        position: linkOffset.position,
      });
    }

    link.prop(['labels', 1, 'attrs', 'label', 'text'], this.createLinkOffsetString(linkOffset));
    if (this.shouldShowOffsetLabel(linkOffset?.offset)) {
      this.showLabel(true, id, 1);
    } else {
      this.showLabel(false, id, 1);
    }
  }

  private shouldShowOffsetLabel(offset: TimeSpanDto): boolean {
    if (offset == undefined || offset.type == undefined) {
      return false;
    }

    if (!offset.from) {
      return false;
    }

    return !(offset.type === TimeSpanType.Range && !offset.to);
  }

  showLabel(show: boolean, id: string, label: number) {
    const link = this.graphSource.graph.getCell(id) as joint.dia.Link;
    const value = show ? 'block' : 'none';
    link.prop(['labels', label, 'attrs', 'body', 'display'], value);
    link.prop(['labels', label, 'attrs', 'label', 'display'], value);
  }

  createLinkOffsetString(linkOffset: LinkOffsetDto): string {
    let offsetText = this.translocoService.translate('Flowchart.Links.Offset.Default');
    const offsetFrom = linkOffset?.offset?.from;
    const offsetTo = linkOffset?.offset?.to;
    if (linkOffset) {
      switch (linkOffset?.offset?.type) {
        case TimeSpanType.Exact:
          offsetText = offsetFrom ? this.translocoService.translate('Flowchart.Links.Offset.Exactly') + ' ' + offsetFrom : offsetText;
          break;
        case TimeSpanType.Range:
          offsetText =
            offsetFrom && offsetTo
              ? this.translocoService.translate('Flowchart.Links.Offset.RangeFrom') +
                ' ' +
                offsetFrom +
                ' ' +
                this.translocoService.translate('Flowchart.Links.Offset.To') +
                ' ' +
                offsetTo
              : offsetText;
          break;
        case TimeSpanType.Min:
          offsetText = offsetFrom ? this.translocoService.translate('Flowchart.Links.Offset.Minimum') + ' ' + offsetFrom : offsetText;
          break;
        case TimeSpanType.Max:
          offsetText = offsetFrom ? this.translocoService.translate('Flowchart.Links.Offset.Maximum') + ' ' + offsetFrom : offsetText;
          break;
        default:
          offsetText = offsetFrom ? this.translocoService.translate('Flowchart.Links.Offset.Default') : offsetText;
          break;
      }
    }
    return offsetText;
  }

  createTextBlockInDefaultPosition(
    sourceElement: joint.dia.ElementView,
    sourceAnchor: LinkAnchorData,
    overwrittenPosition?: joint.g.PlainPoint,
  ): joint.dia.Cell {
    if (overwrittenPosition) {
      return FlowchartTextBlockNode.create(this.graphSource.graph, {
        x: overwrittenPosition.x,
        y: overwrittenPosition.y,
        placeholder: this.translocoService.translate(FlowchartConfig.textBlockNode.placeholder),
      });
    }

    const textblockPosition = this.positionService.calculateStepPosition(
      sourceElement.model.attributes.size,
      sourceElement.model.attributes.position,
      sourceAnchor,
      FlowchartConfig.textBlockNode.size,
    );

    return FlowchartTextBlockNode.create(this.graphSource.graph, {
      x: textblockPosition.x,
      y: textblockPosition.y,
      placeholder: this.translocoService.translate(FlowchartConfig.textBlockNode.placeholder),
    });
  }

  addTextBlock(
    sourceElement: joint.dia.ElementView,
    sourceAnchor: LinkAnchorData,
    targetAnchor: LinkAnchorData,
    newElement: joint.dia.Cell,
    isSubflow: boolean,
    isSubflowLinkedElement: boolean,
  ): void {
    if (isSubflow) {
      this.handleSubflowOperations(sourceElement, newElement, sourceAnchor, targetAnchor, isSubflowLinkedElement);
    } else {
      this.handleNonSubflowOperations(sourceElement, newElement, sourceAnchor, targetAnchor);
    }
    this.fitPaperToContent();
  }

  private fitPaperToContent(): void {
    this.paperSource.paper.fitToContent({ useModelGeometry: true, padding: 10, allowNewOrigin: 'any' });
  }

  private isHtmlContentEmpty(html: string | null | undefined): boolean {
    if (!html) {
      return true;
    }

    const textContent = html.replace(/<[^>]*>/g, '').trim();

    return textContent.length === 0;
  }

  // TODO-REFACTOR: This method both adds and resets tags. It should be split into two methods.
  public addElementTags(dto: Partial<FlowchartStartNodeDto & FlowchartActionDto & FlowchartDecisionNodeDto & FlowchartTextBlockDto>): void {
    if (!dto) {
      return;
    }

    const { subComponentNode } = FlowchartConfig.containerNode;
    const element = this.graphSource.graph.getCell(dto.id);
    const positionX = element?.attributes?.position?.x;
    const positionY = element?.attributes?.position?.y;
    const showDescription = dto.showDescription;

    if (element !== null && element !== undefined) {
      const tagsArray: string[] = [];
      let hasHeader = false;
      const tags: {
        preferredTreatmentOption;
        significance;
        attachments;
        recurrence;
        recurrenceIntervalFrom;
        recurrenceIntervalTo;
        recurrenceIntervalType;
        medicationRequests;
        description;
        atc;
      } = {
        preferredTreatmentOption: null,
        significance: null,
        attachments: null,
        recurrence: null,
        recurrenceIntervalFrom: null,
        recurrenceIntervalTo: null,
        recurrenceIntervalType: null,
        medicationRequests: null,
        description: null,
        atc: null,
      };

      //remove before add
      this.removeElementTags(dto.id);

      if (element?.attributes?.hasHeader || element?.attributes.isSubflow) {
        hasHeader = true;
      }

      tags.attachments = dto.attachments;

      //unify logic tags and tags array
      switch (dto.elementType) {
        case FlowchartElementType.FlowchartAction:
        case FlowchartElementType.FlowchartSubflow:
        case FlowchartElementType.FlowchartContainer:
          if (dto.guidelineAction) {
            tags.description = dto.guidelineAction?.description;
            tags.significance = dto.guidelineAction?.significance;

            tags.preferredTreatmentOption = dto.guidelineAction?.preferredOptionOrder;
            if (dto.guidelineAction?.medicationRequests?.length > 0) {
              tags.medicationRequests = dto.guidelineAction?.medicationRequests;
            }
          }
          tags.recurrence = dto.guidelineAction?.recurrence;
          tags.recurrenceIntervalFrom = dto.guidelineAction?.recurrenceInterval?.from;
          tags.recurrenceIntervalTo = dto.guidelineAction?.recurrenceInterval?.to;
          tags.recurrenceIntervalType = dto.guidelineAction?.recurrenceInterval?.type;
          break;

        case FlowchartElementType.FlowchartDecisionNode:
          if (dto.guidelineObservation) {
            tags.description = dto.guidelineObservation?.description;
          } else if (dto.guidelineCriteria) {
            tags.description = dto.guidelineCriteria?.description;
          } else {
            tags.description = dto.ruleDescription;
            tags.attachments = dto.attachments;
          }
          break;

        case FlowchartElementType.FlowchartStartNode:
          if (dto.guidelineAction) {
            tags.description = dto.guidelineAction?.description;
          } else if (dto.guidelineCriteria) {
            tags.description = dto.guidelineCriteria?.description;
          }
          break;

        case FlowchartElementType.FlowchartTextBlock:
          break;
      }

      if (this.isHtmlContentEmpty(tags?.description)) {
        tags.description = undefined;
      }

      if (tags?.description && !showDescription) {
        tagsArray.push('Description');
      }

      if (tags?.attachments?.length > 0) {
        tagsArray.push('Attachments');
      }

      if (tags?.medicationRequests) {
        tagsArray.push('MedicationGroup');
      }

      if (dto.references?.length > 0) {
        tagsArray.push(IconTagTypes.References);
      }

      if (dto?.links?.length > 0) {
        tagsArray.push('Link');
      }

      if (tags?.recurrence) {
        let recurrenceString;
        if (tags.recurrence == '-1') {
          tagsArray.push('Infinite');
        } else {
          recurrenceString = `${tags.recurrence}x`;
          tagsArray.push(recurrenceString);
        }
      }
      if (tags?.preferredTreatmentOption) {
        tagsArray.push('Preferred');
      }
      if (tags?.significance) {
        tagsArray.push('Optional');
      }

      let totalWidth = 0;
      const elementsToEmbed = [];
      let resourceTagsXOffset = 0;

      if (dto.resourceTags?.length > 0) {
        const positionYCalculated = (
          hasHeader ? positionY + subComponentNode.styles.verticalPadding : positionY + FlowchartConfig.tag.padding.verticalPadding
        ) as number;

        dto.resourceTags.forEach((resourceTag) => {
          const tagWidth =
            calculateTextWidth(resourceTag.label, FontAttributes.fontFamily, '12px') + FlowchartConfig.tag.padding.horizontalPadding;
          const tagXPosition = positionX + FlowchartConfig.tag.padding.horizontalPaddingStart + resourceTagsXOffset;

          elementsToEmbed.push(
            this.createElementTag(
              resourceTag.label,
              tagXPosition,
              positionYCalculated,
              'element--tag__body',
              'element--tag__text',
              resourceTag.backgroundColor,
              resourceTag.color,
            ),
          );

          resourceTagsXOffset += tagWidth + FlowchartConfig.tag.padding.paddingBetweenTags;
          totalWidth += tagWidth + FlowchartConfig.tag.padding.paddingBetweenTags;
        });
      }

      if (tagsArray?.length > 0) {
        tagsArray.forEach((tag, index) => {
          const positionYCalculated = (
            hasHeader ? positionY + subComponentNode.styles.verticalPadding : positionY + FlowchartConfig.tag.padding.verticalPadding
          ) as number;
          const bodyClass = tag == 'Preferred' ? 'element--tag__body_preferred' : 'element--tag__body';
          const textClass = tag == 'Preferred' ? 'element--tag__text_preferred' : 'element--tag__text';
          const colorOverride = tag == 'Preferred' ? '#eafff8' : undefined;
          const textColorOverride = tag == 'Preferred' ? '#10b981' : undefined;
          const translatedTag = this.translocoService.translate(tag);
          const tagXPosition = this.positionService.calculateTagXPosition(
            positionX + resourceTagsXOffset,
            index,
            tagsArray,
            this.translocoService,
          );

          switch (tag) {
            case IconTagTypes.Attachments:
              elementsToEmbed.push(
                this.createIconTag(
                  dto.uniqueId,
                  tagXPosition,
                  positionYCalculated,
                  FLOWCHART_ATTACHMENT_SVG,
                  'isAttachment',
                  dto.colorPresetId,
                  dto.elementType,
                ),
              );
              totalWidth += FlowchartConfig.tag.size.iconTagWidth + FlowchartConfig.tag.padding.paddingBetweenTags;
              break;
            case IconTagTypes.MedicationGroup:
              elementsToEmbed.push(
                this.createIconTag(
                  dto.uniqueId,
                  tagXPosition,
                  positionYCalculated,
                  FLOWCHART_MEDICATION_SVG,
                  'isMedication',
                  dto.colorPresetId,
                  dto.elementType,
                ),
              );
              totalWidth += FlowchartConfig.tag.size.iconTagWidth + FlowchartConfig.tag.padding.paddingBetweenTags;
              break;
            case IconTagTypes.Description:
              elementsToEmbed.push(
                this.createIconTag(
                  dto.uniqueId,
                  tagXPosition,
                  positionYCalculated,
                  FLOWCHART_NOTE_SVG,
                  'isDescription',
                  dto.colorPresetId,
                  dto.elementType,
                ),
              );
              totalWidth += FlowchartConfig.tag.size.iconTagWidth + FlowchartConfig.tag.padding.paddingBetweenTags;
              break;
            case IconTagTypes.Link:
              elementsToEmbed.push(
                this.createIconTag(
                  dto.uniqueId,
                  tagXPosition,
                  positionYCalculated,
                  FLOWCHART_LINK_SVG,
                  'isLink',
                  dto.colorPresetId,
                  dto.elementType,
                ),
              );
              totalWidth += FlowchartConfig.tag.size.iconTagWidth + FlowchartConfig.tag.padding.paddingBetweenTags;
              break;
            case IconTagTypes.Note:
              elementsToEmbed.push(
                this.createIconTag(
                  dto.uniqueId,
                  tagXPosition,
                  positionYCalculated,
                  FLOWCHART_COMMENT_SVG,
                  'isNote',
                  dto.colorPresetId,
                  dto.elementType,
                ),
              );
              totalWidth += FlowchartConfig.tag.size.iconTagWidth + FlowchartConfig.tag.padding.paddingBetweenTags;
              break;
            case IconTagTypes.Infinite:
              elementsToEmbed.push(
                this.createIconTag(
                  dto.uniqueId,
                  tagXPosition,
                  positionYCalculated,
                  FLOWCHART_RECURRENCE_INFINITE_SVG,
                  'isInfinite',
                  dto.colorPresetId,
                  dto.elementType,
                ),
              );
              totalWidth += FlowchartConfig.tag.size.iconTagWidth + FlowchartConfig.tag.padding.paddingBetweenTags;
              break;
            case IconTagTypes.References:
              elementsToEmbed.push(
                this.createIconTag(
                  dto.uniqueId,
                  tagXPosition,
                  positionYCalculated,
                  FLOWCHART_REFERENCE_SVG,
                  'isReferences',
                  dto.colorPresetId,
                  dto.elementType,
                ),
              );
              break;
            default:
              elementsToEmbed.push(
                this.createElementTag(
                  translatedTag,
                  tagXPosition,
                  positionYCalculated,
                  bodyClass,
                  textClass,
                  colorOverride,
                  textColorOverride,
                  dto.colorPresetId,
                  dto.elementType,
                ),
              );
              totalWidth +=
                calculateTextWidth(translatedTag, FontAttributes.fontFamily, '12px') +
                FlowchartConfig.tag.padding.horizontalPadding +
                FlowchartConfig.tag.padding.paddingBetweenTags;
              break;
          }
        });
        totalWidth += FlowchartConfig.tag.padding.horizontalPadding;
      }

      const elementModel = this.paperSource.paper.getModelById(dto.id) as joint.dia.Element;
      if (element?.attributes.size.width < totalWidth) {
        elementModel.resize(
          ContainerResize.getSizeToGrid({ width: totalWidth, height: element.attributes.size.height }).width,
          elementModel.attributes.size.height,
        );
      }

      elementsToEmbed.forEach((elementToEmbedd) => {
        this.graphSource.graph.addCell(elementToEmbedd);
        element?.embed(elementToEmbedd);
      });

      if (elementsToEmbed.length > 0) {
        this.paperSource.paper.updateViews();
        requestAnimationFrame(() => this.renderIconSvgs(elementsToEmbed));
      }

      //adapt container size and position of elements
      if (elementModel?.attributes?.parentContainerId) {
        ContainerResize.adjustContainerSizeOnEmbedChange(
          this.graphSource.graph.getCell(elementModel?.attributes?.parentContainerId) as joint.dia.Element,
          this.paperSource.paper,
        );
        ContainerResize.adjustWidthOfContainer(
          this.graphSource.graph.getCell(elementModel?.attributes?.parentContainerId) as joint.dia.Element,
          this.paperSource.paper,
        );
        ContainerResize.repositionContainerElements(
          this.graphSource.graph.getCell(elementModel?.attributes?.parentContainerId) as joint.dia.Element,
          this.paperSource.paper,
        );
      }

      this.setLabelPosition(dto.id);
    }
  }

  ensureMinimumIncomingLinkLength(cell: joint.dia.Cell): void {
    const incomingLinks = this.graphSource.graph.getConnectedLinks(cell, { inbound: true });
    if (incomingLinks.length !== 1) {
      return;
    }

    const link = incomingLinks[0] as joint.dia.Link;
    const linkView = link.findView(this.paperSource.paper) as joint.dia.LinkView;
    if (!linkView) {
      return;
    }

    this.paperSource.paper.updateViews();

    const sourcePoint = linkView.getEndAnchor('source');
    const targetPoint = linkView.getEndAnchor('target');
    if (!sourcePoint || !targetPoint) {
      return;
    }

    const currentLength = new joint.g.Point(sourcePoint.x, sourcePoint.y).distance(new joint.g.Point(targetPoint.x, targetPoint.y));
    if (currentLength === 0 || currentLength >= MINIMUM_INCOMING_LINK_LENGTH) {
      return;
    }

    const unitX = (targetPoint.x - sourcePoint.x) / currentLength;
    const unitY = (targetPoint.y - sourcePoint.y) / currentLength;
    const extendBy = MINIMUM_INCOMING_LINK_LENGTH - currentLength;

    const element = cell as joint.dia.Element;
    const currentPosition = element.position();
    const newX = getGridMatchedPositionValue(currentPosition.x + unitX * extendBy);
    const newY = getGridMatchedPositionValue(currentPosition.y + unitY * extendBy);

    element.translate(newX - currentPosition.x, newY - currentPosition.y);
  }

  scrollToElement(newElement: joint.dia.Cell) {
    const elementView = this.paperSource.paper.model.getElements().find((el) => el.id === newElement.id);
    const isElementVisible = this.scrollerSource.isElementVisible(elementView, { strict: true });

    if (isElementVisible) {
      return;
    }

    const visibleArea = this.scrollerSource.getVisibleArea();
    const scale = this.paperSource.paper.scale().sx;
    const { x: minX, y: minY, width, height } = visibleArea;
    const maxX = minX + width;
    const maxY = minY + height;
    const centerX = minX + width / 2;
    const centerY = minY + height / 2;

    const { x: elMinX, y: elMinY, width: elWidth, height: elHeight } = newElement.getBBox();
    const elMaxX = elMinX + elWidth;
    const elMaxY = elMinY + elHeight;

    let newCenterX = centerX * scale;
    let newCenterY = centerY * scale;

    if (elMinX < minX) {
      newCenterX += (elMinX - minX - AUTO_SCROLL_HORIZONTAL_MARGIN) * scale;
    }

    if (elMaxX > maxX) {
      newCenterX += (elMaxX - maxX + AUTO_SCROLL_HORIZONTAL_MARGIN) * scale;
    }

    if (elMinY < minY) {
      newCenterY += (elMinY - minY - AUTO_SCROLL_VERTICAL_MARGIN) * scale;
    }

    if (elMaxY > maxY) {
      newCenterY += (elMaxY - maxY + AUTO_SCROLL_VERTICAL_MARGIN) * scale;
    }

    const scaledCenterX = centerX * scale;
    const scaledCenterY = centerY * scale;
    if (newCenterX !== scaledCenterX || newCenterY !== scaledCenterY) {
      this.scrollerSource.scroll(newCenterX, newCenterY, { animation: { duration: 250 } });
    }
  }

  private removeElementTags(elementId: string) {
    const element = this.graphSource.graph.getCell(elementId);
    const embededTags = element?.getEmbeddedCells();

    embededTags?.forEach((tag) => {
      if (tag?.attributes?.isElementTag) {
        tag.remove();
      }
    });

    this.setLabelPosition(elementId);

    if (element?.attributes?.parentContainerId) {
      ContainerResize.adjustWidthOfContainer(
        this.graphSource.graph.getCell(element?.attributes?.parentContainerId) as joint.dia.Element,
        this.paperSource.paper,
      );
      ContainerResize.repositionContainerElements(
        this.graphSource.graph.getCell(element?.attributes?.parentContainerId) as joint.dia.Element,
        this.paperSource.paper,
      );
      ContainerResize.adjustContainerSizeOnEmbedChange(
        this.graphSource.graph.getCell(element?.attributes?.parentContainerId) as joint.dia.Element,
        this.paperSource.paper,
      );
    }
  }

  private createElementTag(
    text: string,
    x: number,
    y: number,
    classBody: string,
    classText: string,
    colorOverride?: string,
    textColorOverride?: string,
    colorPresetId?: string,
    elementType?: FlowchartElementType,
  ): joint.dia.Cell {
    const tagConfigValues = FlowchartConfig.tag;
    const actionPosition = {
      x: x,
      y: y,
    };

    const customisedId = elementType ? ELEMENT_TYPE_TO_CUSTOMISED_ID[elementType] : undefined;

    const resolvedFill = colorOverride
      ? colorOverride
      : colorPresetId
        ? `var(--tt-color-preset-${colorPresetId}-text) !important`
        : customisedId
          ? `var(--tt-customisation-${customisedId}-text, var(--tt-flowchart-tag-fill))`
          : '#EFF4F9';
    const resolvedTextColor = textColorOverride
      ? textColorOverride
      : colorPresetId
        ? `var(--tt-color-preset-${colorPresetId}-fill) !important`
        : customisedId
          ? `var(--tt-customisation-${customisedId}-fill, var(--tt-flowchart-tag-text))`
          : '#063d70';

    const elementTag = new joint.shapes.standard.Rectangle({
      position: actionPosition,
      size: {
        width: calculateTextWidth(text, FontAttributes.fontFamily, '12px') + FlowchartConfig.tag.padding.horizontalPadding,
        height: tagConfigValues.size.height,
      },
      z: tagConfigValues.zIndex,
      attrs: {
        body: {
          class: classBody,
          rx: tagConfigValues.body.rx,
          ry: tagConfigValues.body.ry,
          fill: resolvedFill,
          stroke: resolvedFill,
        },
        label: {
          class: classText,
          ...FontAttributes,
          fontSize: FontAttributesTags.fontSizeTags,
          lineHeight: FontAttributesTags.lineHeightTags,
          fill: resolvedTextColor,
          text,
          textWrap: {
            width: tagConfigValues.textWrap.width,
            ellipsis: true,
          },
          textVerticalAnchor: 'bottom',
          transform: 'translate(0px, 8px)',
        },
      },
    });
    elementTag.set('isElementTag', true);

    return elementTag;
  }

  private renderIconSvgs(elements: joint.dia.Cell[]): void {
    elements.forEach((iconElement) => {
      if (iconElement.attributes.isElementTag && iconElement.attributes.iconSvg) {
        const view = this.paperSource.paper.findViewByModel(iconElement);
        if (view) {
          const iconContainer = view.vel.findOne('[joint-selector="iconContainer"]');
          if (iconContainer) {
            const parser = new DOMParser();
            const svgDoc = parser.parseFromString(iconElement.attributes.iconSvg as string, 'image/svg+xml');
            const svgElement = svgDoc.documentElement;

            svgElement.setAttribute('width', FlowchartConfig.tag.size.iconWidth.toString());
            svgElement.setAttribute('height', FlowchartConfig.tag.size.iconHeight.toString());

            const importedSvg = document.importNode(svgElement, true);
            iconContainer.node.appendChild(importedSvg);
          }
        }
      }
    });
  }

  private createIconTag(
    elementId: string,
    x: number,
    y: number,
    icon: string,
    attribute?: string,
    colorPresetId?: string,
    elementType?: FlowchartElementType,
  ): joint.dia.Cell {
    const tagConfigValues = FlowchartConfig.tag;
    const actionPosition = {
      x: x,
      y: y,
    };

    const attributeDataTestIdMap = {
      isDescription: 'flowchart-node-tag_description',
      isAttachment: 'flowchart-node-tag_attachment',
      isMedication: 'flowchart-node-tag_medication',
      isLink: 'flowchart-node-tag_link',
    };

    const colorName = `var(--tt-customisation-${ELEMENT_TYPE_TO_CUSTOMISED_ID[elementType]}-text, var(--tt-flowchart-${ELEMENT_TYPE_TO_CUSTOMISED_ID[elementType]}-text))`;

    const iconTag = new joint.shapes.standard.Rectangle({
      position: actionPosition,
      size: {
        width: FlowchartConfig.tag.size.iconTagWidth,
        height: FlowchartConfig.tag.size.iconTagHeight,
      },
      z: tagConfigValues.zIndex,
      markup: [
        {
          tagName: 'rect',
          selector: 'body',
        },
        {
          tagName: 'g',
          selector: 'iconContainer',
        },
      ],
      attrs: {
        root: {
          'data-testid': attributeDataTestIdMap[attribute] || 'flowchart-tag',
          'data-flowchart-element-id': elementId,
        },
        body: {
          class: 'element--tag__body image',
          rx: tagConfigValues.body.rx,
          ry: tagConfigValues.body.ry,
          fill: 'transparent',
          stroke: 'transparent',
        },
        iconContainer: {
          class: 'element--tag__icon',
          color: colorPresetId ? `var(--tt-color-preset-${colorPresetId}-text) !important` : colorName,
          transform: `translate(${(FlowchartConfig.tag.size.iconTagWidth - FlowchartConfig.tag.size.iconWidth) / 2}, ${(FlowchartConfig.tag.size.iconTagHeight - FlowchartConfig.tag.size.iconHeight) / 2})`,
        },
      },
    });

    // Store the icon SVG string for later rendering
    iconTag.set('iconSvg', icon);

    iconTag.set('isElementTag', true);
    iconTag.set(attribute, true);

    return iconTag;
  }
  private setLabelPosition(elementId: string) {
    const element = this.graphSource.graph.getCell(elementId);
    const hasTags = element?.getEmbeddedCells()?.find((embed) => embed.attributes.isElementTag);
    const hasDescription = element?.getEmbeddedCells()?.find((embed) => embed.attributes.isElementDescription);
    if (element) {
      if (hasDescription && hasTags) {
        //if description and tags place title in between an set max height to not exceed
        const tagAdjustment =
          FlowchartConfig.tag.size.height +
          FlowchartConfig.tag.padding.verticalTextPadding +
          FlowchartConfig.tag.padding.paddingBetweenTagsAndLabel;
        element.attr('label', {
          textWrap: {
            height: 50,
            ellipsis: true,
            maxLineCount: 2,
          },
          y: `-calc(0.5*h - ${tagAdjustment})`,
        });
      } else if (!hasDescription && hasTags) {
        element.attr('label', {
          y: FlowchartConfig.tag.padding.verticalTextPadding.toString(),
        });
      } else if (hasDescription && !hasTags) {
        //if description and no tags label should move up
        element.attr('label', {
          y: `-calc(0.5*h - ${FlowchartConfig.label.top.padding})`,
          textWrap: {
            height: 50,
            ellipsis: true,
            maxLineCount: 2,
          },
        });
      } else {
        //if no tags and no description Label should be in the middle
        element.attr('label', {
          y: 0,
        });
      }
    }
  }
}
export default OperationsService;
